import React, { useEffect, useState } from "react";
import { supabase } from "../supabaseClient";
import { Link } from "react-router-dom";
import {
  ArrowLeft,
  PlusCircle,
  Trash2,
  Search,
  CheckCircle,
  XCircle,
  Users,
  Filter,
  Edit3,
  X,
  CheckSquare,
  Square,
  Calendar,
  RefreshCw, // Ikon Reset
} from "lucide-react";

export default function LaporanKeuangan() {
  const [activeTab, setActiveTab] = useState("list"); // 'list' | 'create'
  const [tagihanList, setTagihanList] = useState([]);
  const [siswaList, setSiswaList] = useState([]);
  const [loading, setLoading] = useState(true);

  // --- STATE FILTER TABEL (BARU) ---
  const [filterList, setFilterList] = useState({
    nama: "",
    jenis: "",
    tanggal: "",
    status: "",
  });

  // STATE BULK CREATE
  const [selectedSiswaIDs, setSelectedSiswaIDs] = useState([]);
  const [bulkForm, setBulkForm] = useState({ judul: "", jumlah: "" });
  const [isSubmitting, setIsSubmitting] = useState(false);

  // STATE FILTER BULK (Pencarian di Tab Buat Tagihan)
  const [bulkSearch, setBulkSearch] = useState("");
  const [bulkKelas, setBulkKelas] = useState("");

  // STATE EDIT SINGLE
  const [editingItem, setEditingItem] = useState(null);

  // TOAST
  const [toast, setToast] = useState({ show: false, msg: "", type: "success" });

  // Opsi Kelas
  const opsiKelas = [
    "TK",
    "1 SD",
    "2 SD",
    "3 SD",
    "4 SD",
    "5 SD",
    "6 SD",
    "7 SMP",
    "8 SMP",
    "9 SMP",
    "10 SMA",
    "11 SMA",
    "12 SMA",
    "Umum",
  ];

  useEffect(() => {
    fetchData();
  }, []);

  const showToast = (msg, type = "success") => {
    setToast({ show: true, msg, type });
    setTimeout(() => setToast((prev) => ({ ...prev, show: false })), 3000);
  };

  // Helper Format Tanggal
  const formatTanggal = (dateString) => {
    if (!dateString) return "-";
    return new Date(dateString).toLocaleDateString("id-ID", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  };

  async function fetchData() {
    setLoading(true);
    const { data: dtTagihan } = await supabase
      .from("tagihan")
      .select("*, siswa(nama_lengkap, kelas)")
      .order("id", { ascending: false });
    const { data: dtSiswa } = await supabase
      .from("siswa")
      .select("*")
      .order("nama_lengkap");
    setTagihanList(dtTagihan || []);
    setSiswaList(dtSiswa || []);
    setLoading(false);
  }

  // --- LOGIKA FILTER TABEL UTAMA (BARU) ---
  const getFilteredTagihan = () => {
    return tagihanList.filter((item) => {
      // 1. Filter Nama Siswa
      const matchNama = item.siswa?.nama_lengkap
        .toLowerCase()
        .includes(filterList.nama.toLowerCase());

      // 2. Filter Jenis Tagihan (Judul)
      const matchJenis = item.judul_tagihan
        .toLowerCase()
        .includes(filterList.jenis.toLowerCase());

      // 3. Filter Status
      const matchStatus = filterList.status
        ? item.status === filterList.status
        : true;

      // 4. Filter Tanggal Bayar
      // (Hanya jika user memilih tanggal, kita bandingkan string YYYY-MM-DD)
      const matchTanggal = filterList.tanggal
        ? item.tanggal_bayar &&
          item.tanggal_bayar.startsWith(filterList.tanggal)
        : true;

      return matchNama && matchJenis && matchStatus && matchTanggal;
    });
  };

  const filteredTagihan = getFilteredTagihan();

  // Reset Filter Helper
  const resetFilter = () => {
    setFilterList({ nama: "", jenis: "", tanggal: "", status: "" });
  };

  // --- LOGIKA FILTER SISWA (Create Tab) ---
  const getFilteredSiswa = () => {
    return siswaList.filter((s) => {
      const matchName = s.nama_lengkap
        .toLowerCase()
        .includes(bulkSearch.toLowerCase());
      const matchKelas = bulkKelas ? s.kelas === bulkKelas : true;
      const matchStatus = s.status === "Aktif";
      return matchName && matchKelas && matchStatus;
    });
  };

  const filteredSiswa = getFilteredSiswa();

  // --- LOGIKA BULK INSERT ---
  const handleSelectSiswa = (id) => {
    if (selectedSiswaIDs.includes(id)) {
      setSelectedSiswaIDs(selectedSiswaIDs.filter((item) => item !== id));
    } else {
      setSelectedSiswaIDs([...selectedSiswaIDs, id]);
    }
  };

  const handleSelectAllVisible = () => {
    const visibleIDs = filteredSiswa.map((s) => s.id);
    const isAllVisibleSelected = visibleIDs.every((id) =>
      selectedSiswaIDs.includes(id)
    );

    if (isAllVisibleSelected) {
      setSelectedSiswaIDs((prev) =>
        prev.filter((id) => !visibleIDs.includes(id))
      );
    } else {
      const newSet = new Set([...selectedSiswaIDs, ...visibleIDs]);
      setSelectedSiswaIDs(Array.from(newSet));
    }
  };

  const handleBulkSubmit = async (e) => {
    e.preventDefault();
    if (selectedSiswaIDs.length === 0)
      return showToast("Pilih minimal 1 siswa!", "error");
    if (!bulkForm.judul || !bulkForm.jumlah)
      return showToast("Judul dan Jumlah wajib diisi!", "error");

    setIsSubmitting(true);
    try {
      const payload = selectedSiswaIDs.map((siswaId) => ({
        siswa_id: siswaId,
        judul_tagihan: bulkForm.judul,
        jumlah: parseInt(bulkForm.jumlah),
        status: "BELUM",
        tanggal_bayar: null,
      }));

      const { error } = await supabase.from("tagihan").insert(payload);
      if (error) throw error;

      showToast(`Berhasil membuat ${payload.length} tagihan baru!`);
      setBulkForm({ judul: "", jumlah: "" });
      setSelectedSiswaIDs([]);
      setActiveTab("list");
      fetchData();
    } catch (err) {
      showToast("Gagal: " + err.message, "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  // --- LOGIKA EDIT & HAPUS ---
  const toggleStatus = async (item) => {
    const newStatus = item.status === "LUNAS" ? "BELUM" : "LUNAS";
    const newTanggal =
      newStatus === "LUNAS" ? new Date().toISOString().split("T")[0] : null;

    try {
      await supabase
        .from("tagihan")
        .update({
          status: newStatus,
          tanggal_bayar: newTanggal,
        })
        .eq("id", item.id);
      fetchData();
      showToast(`Status diubah jadi ${newStatus}`);
    } catch (error) {
      showToast("Gagal update status", "error");
    }
  };

  const handleDelete = async (id) => {
    if (!confirm("Hapus tagihan ini?")) return;
    try {
      await supabase.from("tagihan").delete().eq("id", id);
      fetchData();
      showToast("Tagihan dihapus");
    } catch (error) {
      showToast("Gagal hapus", "error");
    }
  };

  const handleEditSave = async (e) => {
    e.preventDefault();
    try {
      const { error } = await supabase
        .from("tagihan")
        .update({
          judul_tagihan: editingItem.judul_tagihan,
          jumlah: editingItem.jumlah,
          tanggal_bayar: editingItem.tanggal_bayar || null,
        })
        .eq("id", editingItem.id);
      if (error) throw error;
      showToast("Perubahan disimpan!");
      setEditingItem(null);
      fetchData();
    } catch (error) {
      showToast("Gagal update: " + error.message, "error");
    }
  };

  // --- STYLES ---
  const styles = {
    container: {
      padding: "20px",
      maxWidth: "1000px",
      margin: "0 auto",
      fontFamily: "'Inter', sans-serif",
    },
    tabContainer: { display: "flex", gap: "10px", marginBottom: "20px" },
    tabBtn: (isActive) => ({
      padding: "12px 20px",
      borderRadius: "10px",
      border: "none",
      cursor: "pointer",
      fontWeight: "600",
      display: "flex",
      alignItems: "center",
      gap: "8px",
      fontSize: "14px",
      background: isActive ? "#2563EB" : "white",
      color: isActive ? "white" : "#64748B",
      boxShadow: isActive
        ? "0 4px 6px -1px rgba(37, 99, 235, 0.2)"
        : "0 1px 2px rgba(0,0,0,0.05)",
      border: isActive ? "none" : "1px solid #E2E8F0",
    }),
    card: {
      background: "white",
      borderRadius: "16px",
      border: "1px solid #E2E8F0",
      padding: "20px",
      boxShadow: "0 4px 6px -1px rgba(0,0,0,0.05)",
    },
    input: {
      width: "100%",
      padding: "12px",
      borderRadius: "8px",
      border: "1px solid #CBD5E1",
      marginBottom: "15px",
      boxSizing: "border-box",
      outline: "none",
    },
    badge: (status) => ({
      padding: "4px 10px",
      borderRadius: "20px",
      fontSize: "11px",
      fontWeight: "700",
      cursor: "pointer",
      background: status === "LUNAS" ? "#DCFCE7" : "#FEE2E2",
      color: status === "LUNAS" ? "#166534" : "#991B1B",
      border: "none",
      display: "flex",
      alignItems: "center",
      gap: 4,
      width: "fit-content",
    }),
    filterBar: {
      display: "flex",
      gap: 10,
      marginBottom: 15,
      background: "#F8FAFC",
      padding: 10,
      borderRadius: 8,
      border: "1px solid #F1F5F9",
    },
    searchSmall: {
      flex: 1,
      padding: "8px 12px 8px 30px",
      borderRadius: 6,
      border: "1px solid #CBD5E1",
      fontSize: 13,
      outline: "none",
    },
    selectSmall: {
      padding: "8px",
      borderRadius: 6,
      border: "1px solid #CBD5E1",
      fontSize: 13,
      outline: "none",
      cursor: "pointer",
      background: "white",
    },
    modalOverlay: {
      position: "fixed",
      top: 0,
      left: 0,
      width: "100%",
      height: "100%",
      background: "rgba(0,0,0,0.5)",
      zIndex: 999,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    },
    // Style Khusus Filter Utama
    mainFilterGrid: {
      display: "grid",
      gridTemplateColumns: "1.5fr 1.5fr 1fr 1fr auto", // Layout Kolom
      gap: "10px",
      marginBottom: "20px",
      background: "#F1F5F9",
      padding: "15px",
      borderRadius: "12px",
      alignItems: "center",
    },
    filterInput: {
      width: "100%",
      padding: "10px",
      borderRadius: "8px",
      border: "1px solid #CBD5E1",
      fontSize: "13px",
      boxSizing: "border-box",
      outline: "none",
    },
  };

  return (
    <div style={styles.container}>
      {toast.show && (
        <div
          style={{
            position: "fixed",
            top: 20,
            left: "50%",
            transform: "translateX(-50%)",
            background: toast.type === "success" ? "#065F46" : "#991B1B",
            color: "white",
            padding: "10px 20px",
            borderRadius: 8,
            zIndex: 1000,
            fontSize: 14,
            fontWeight: "bold",
            boxShadow: "0 10px 15px -3px rgba(0,0,0,0.1)",
          }}
        >
          {toast.msg}
        </div>
      )}

      <Link
        to="/"
        style={{
          display: "flex",
          alignItems: "center",
          gap: "5px",
          textDecoration: "none",
          color: "#64748B",
          marginBottom: "20px",
          fontSize: "14px",
          fontWeight: "500",
        }}
      >
        <ArrowLeft size={18} /> Kembali ke Dashboard
      </Link>

      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 10,
          marginBottom: 25,
        }}
      >
        <div style={{ background: "#F0FDF4", padding: 10, borderRadius: 12 }}>
          <Users size={28} color="#16A34A" />
        </div>
        <div>
          <h2 style={{ margin: 0, color: "#1E293B", fontSize: 22 }}>
            Keuangan & SPP
          </h2>
          <p style={{ margin: 0, color: "#64748B", fontSize: 14 }}>
            Kelola tagihan siswa secara massal
          </p>
        </div>
      </div>

      <div style={styles.tabContainer}>
        <button
          style={styles.tabBtn(activeTab === "list")}
          onClick={() => setActiveTab("list")}
        >
          <Search size={16} /> Daftar Tagihan
        </button>
        <button
          style={styles.tabBtn(activeTab === "create")}
          onClick={() => setActiveTab("create")}
        >
          <PlusCircle size={16} /> Buat Tagihan Massal
        </button>
      </div>

      {/* --- TAB LIST --- */}
      {activeTab === "list" && (
        <div style={styles.card}>
          {/* AREA FILTER BARU */}
          <div style={styles.mainFilterGrid}>
            {/* 1. Filter Nama */}
            <input
              placeholder="Cari Nama Siswa..."
              style={styles.filterInput}
              value={filterList.nama}
              onChange={(e) =>
                setFilterList({ ...filterList, nama: e.target.value })
              }
            />
            {/* 2. Filter Jenis Tagihan */}
            <input
              placeholder="Jenis Tagihan (mis: SPP)"
              style={styles.filterInput}
              value={filterList.jenis}
              onChange={(e) =>
                setFilterList({ ...filterList, jenis: e.target.value })
              }
            />
            {/* 3. Filter Tanggal Bayar */}
            <input
              type="date"
              title="Tanggal Bayar"
              style={styles.filterInput}
              value={filterList.tanggal}
              onChange={(e) =>
                setFilterList({ ...filterList, tanggal: e.target.value })
              }
            />
            {/* 4. Filter Status */}
            <select
              style={styles.filterInput}
              value={filterList.status}
              onChange={(e) =>
                setFilterList({ ...filterList, status: e.target.value })
              }
            >
              <option value="">Semua Status</option>
              <option value="LUNAS">Sudah Lunas</option>
              <option value="BELUM">Belum Lunas</option>
            </select>
            {/* Tombol Reset */}
            <button
              onClick={resetFilter}
              style={{
                background: "white",
                border: "1px solid #CBD5E1",
                borderRadius: "8px",
                padding: "10px",
                cursor: "pointer",
                color: "#64748B",
              }}
              title="Reset Filter"
            >
              <RefreshCw size={18} />
            </button>
          </div>

          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              marginBottom: 15,
            }}
          >
            <h3 style={{ margin: 0, fontSize: 16, color: "#334155" }}>
              Riwayat Transaksi
            </h3>
            <span style={{ fontSize: 12, color: "#64748B" }}>
              Menampilkan: {filteredTagihan.length} dari {tagihanList.length}{" "}
              Data
            </span>
          </div>

          {loading ? (
            <p>Loading...</p>
          ) : (
            <div style={{ overflowX: "auto" }}>
              <table
                style={{
                  width: "100%",
                  borderCollapse: "collapse",
                  fontSize: "14px",
                }}
              >
                <thead>
                  <tr
                    style={{
                      borderBottom: "2px solid #F1F5F9",
                      textAlign: "left",
                      color: "#64748B",
                    }}
                  >
                    <th style={{ padding: 10 }}>Siswa</th>
                    <th style={{ padding: 10 }}>Tagihan</th>
                    <th style={{ padding: 10 }}>Jumlah</th>
                    <th style={{ padding: 10 }}>Tgl Bayar</th>
                    <th style={{ padding: 10 }}>Status</th>
                    <th style={{ padding: 10, textAlign: "right" }}>Aksi</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredTagihan.length === 0 ? (
                    <tr>
                      <td
                        colSpan="6"
                        style={{
                          textAlign: "center",
                          padding: 30,
                          color: "#94A3B8",
                        }}
                      >
                        Tidak ada data yang cocok dengan filter.
                      </td>
                    </tr>
                  ) : (
                    filteredTagihan.map((item) => (
                      <tr
                        key={item.id}
                        style={{ borderBottom: "1px solid #F8FAFC" }}
                      >
                        <td style={{ padding: 10 }}>
                          <div style={{ fontWeight: "bold" }}>
                            {item.siswa?.nama_lengkap}
                          </div>
                          <div style={{ fontSize: 12, color: "#94A3B8" }}>
                            {item.siswa?.kelas}
                          </div>
                        </td>
                        <td style={{ padding: 10 }}>{item.judul_tagihan}</td>
                        <td style={{ padding: 10 }}>
                          Rp {item.jumlah.toLocaleString()}
                        </td>
                        <td
                          style={{
                            padding: 10,
                            fontSize: 13,
                            color: item.tanggal_bayar ? "#334155" : "#CBD5E1",
                          }}
                        >
                          {formatTanggal(item.tanggal_bayar)}
                        </td>
                        <td style={{ padding: 10 }}>
                          <button
                            onClick={() => toggleStatus(item)}
                            style={styles.badge(item.status)}
                            title="Klik untuk ubah status"
                          >
                            {item.status === "LUNAS" ? (
                              <CheckCircle size={12} />
                            ) : (
                              <XCircle size={12} />
                            )}{" "}
                            {item.status}
                          </button>
                        </td>
                        <td style={{ padding: 10, textAlign: "right" }}>
                          <button
                            onClick={() => setEditingItem(item)}
                            style={{
                              background: "#EFF6FF",
                              color: "#3B82F6",
                              border: "none",
                              padding: 6,
                              borderRadius: 6,
                              cursor: "pointer",
                              marginRight: 5,
                            }}
                          >
                            <Edit3 size={16} />
                          </button>
                          <button
                            onClick={() => handleDelete(item.id)}
                            style={{
                              background: "#FEF2F2",
                              color: "#EF4444",
                              border: "none",
                              padding: 6,
                              borderRadius: 6,
                              cursor: "pointer",
                            }}
                          >
                            <Trash2 size={16} />
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* --- BAGIAN CREATE MASSAL (TIDAK BERUBAH) --- */}
      {activeTab === "create" && (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 2fr",
            gap: 20,
            alignItems: "start",
          }}
        >
          {/* KOLOM KIRI: FORM INPUT */}
          <div style={{ position: "sticky", top: 20, ...styles.card }}>
            <h3 style={{ marginTop: 0, color: "#2563EB", fontSize: 16 }}>
              1. Detail Tagihan
            </h3>
            <form onSubmit={handleBulkSubmit}>
              <label
                style={{
                  display: "block",
                  marginBottom: 5,
                  fontSize: 13,
                  fontWeight: 600,
                }}
              >
                Judul Tagihan
              </label>
              <input
                style={styles.input}
                placeholder="Contoh: SPP Januari 2025"
                value={bulkForm.judul}
                onChange={(e) =>
                  setBulkForm({ ...bulkForm, judul: e.target.value })
                }
              />

              <label
                style={{
                  display: "block",
                  marginBottom: 5,
                  fontSize: 13,
                  fontWeight: 600,
                }}
              >
                Jumlah (Rp)
              </label>
              <input
                type="number"
                style={styles.input}
                placeholder="Contoh: 150000"
                value={bulkForm.jumlah}
                onChange={(e) =>
                  setBulkForm({ ...bulkForm, jumlah: e.target.value })
                }
              />

              <div
                style={{
                  background: "#F0F9FF",
                  padding: 15,
                  borderRadius: 8,
                  textAlign: "center",
                  marginBottom: 15,
                  border: "1px dashed #BAE6FD",
                }}
              >
                <div
                  style={{ fontSize: 11, fontWeight: 700, color: "#64748B" }}
                >
                  SISWA TERPILIH
                </div>
                <div
                  style={{
                    fontSize: 32,
                    fontWeight: 800,
                    color: "#0284C7",
                    lineHeight: 1,
                  }}
                >
                  {selectedSiswaIDs.length}
                </div>
              </div>

              <button
                type="submit"
                style={{
                  width: "100%",
                  padding: 12,
                  background: "#2563EB",
                  color: "white",
                  border: "none",
                  borderRadius: 8,
                  fontWeight: "bold",
                  cursor: "pointer",
                  opacity: isSubmitting ? 0.7 : 1,
                }}
                disabled={isSubmitting}
              >
                {isSubmitting
                  ? "Memproses..."
                  : `Buat Tagihan (${selectedSiswaIDs.length})`}
              </button>
            </form>
          </div>

          {/* KOLOM KANAN: PILIH SISWA */}
          <div style={styles.card}>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: 15,
              }}
            >
              <h3 style={{ margin: 0, color: "#334155", fontSize: 16 }}>
                2. Pilih Siswa
              </h3>
              <button
                onClick={handleSelectAllVisible}
                style={{
                  background: "#E2E8F0",
                  border: "none",
                  padding: "6px 12px",
                  borderRadius: 6,
                  fontSize: 12,
                  cursor: "pointer",
                  fontWeight: 600,
                  color: "#475569",
                }}
              >
                Pilih Semua (Yang Tampil)
              </button>
            </div>

            {/* BAR FILTER SISWA */}
            <div style={styles.filterBar}>
              <div style={{ position: "relative", flex: 1 }}>
                <Search
                  size={14}
                  style={{
                    position: "absolute",
                    left: 10,
                    top: 9,
                    color: "#94A3B8",
                  }}
                />
                <input
                  style={styles.searchSmall}
                  placeholder="Cari nama..."
                  value={bulkSearch}
                  onChange={(e) => setBulkSearch(e.target.value)}
                />
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
                <Filter size={16} color="#64748B" />
                <select
                  style={styles.selectSmall}
                  value={bulkKelas}
                  onChange={(e) => setBulkKelas(e.target.value)}
                >
                  <option value="">Semua Kelas</option>
                  {opsiKelas.map((k) => (
                    <option key={k} value={k}>
                      {k}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div
              style={{
                maxHeight: "500px",
                overflowY: "auto",
                border: "1px solid #F1F5F9",
                borderRadius: 8,
              }}
            >
              {filteredSiswa.length === 0 ? (
                <div
                  style={{ padding: 20, textAlign: "center", color: "#94A3B8" }}
                >
                  Siswa tidak ditemukan.
                </div>
              ) : (
                filteredSiswa.map((siswa) => {
                  const isSelected = selectedSiswaIDs.includes(siswa.id);
                  return (
                    <div
                      key={siswa.id}
                      onClick={() => handleSelectSiswa(siswa.id)}
                      style={{
                        padding: "10px 15px",
                        borderBottom: "1px solid #F8FAFC",
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        gap: 10,
                        background: isSelected ? "#EFF6FF" : "white",
                        transition: "0.1s",
                      }}
                    >
                      <div
                        style={{ color: isSelected ? "#2563EB" : "#CBD5E1" }}
                      >
                        {isSelected ? (
                          <CheckSquare size={20} />
                        ) : (
                          <Square size={20} />
                        )}
                      </div>
                      <div>
                        <div style={{ fontWeight: 600, color: "#334155" }}>
                          {siswa.nama_lengkap}
                        </div>
                        <div style={{ fontSize: 11, color: "#94A3B8" }}>
                          {siswa.kelas}{" "}
                          {siswa.kelompok && `• ${siswa.kelompok}`}
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>
      )}

      {/* --- MODAL EDIT --- */}
      {editingItem && (
        <div style={styles.modalOverlay}>
          <div
            style={{
              background: "white",
              padding: 25,
              borderRadius: 16,
              width: "300px",
              boxShadow: "0 20px 25px -5px rgba(0,0,0,0.1)",
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                marginBottom: 20,
              }}
            >
              <h3 style={{ margin: 0 }}>Edit Tagihan</h3>
              <button
                onClick={() => setEditingItem(null)}
                style={{
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                }}
              >
                <X size={20} />
              </button>
            </div>
            <form onSubmit={handleEditSave}>
              <label
                style={{ display: "block", marginBottom: 5, fontSize: 13 }}
              >
                Judul Tagihan
              </label>
              <input
                style={styles.input}
                value={editingItem.judul_tagihan}
                onChange={(e) =>
                  setEditingItem({
                    ...editingItem,
                    judul_tagihan: e.target.value,
                  })
                }
              />
              <label
                style={{ display: "block", marginBottom: 5, fontSize: 13 }}
              >
                Jumlah (Rp)
              </label>
              <input
                type="number"
                style={styles.input}
                value={editingItem.jumlah}
                onChange={(e) =>
                  setEditingItem({ ...editingItem, jumlah: e.target.value })
                }
              />

              <label
                style={{
                  display: "block",
                  marginBottom: 5,
                  fontSize: 13,
                  fontWeight: "bold",
                  color: "#334155",
                }}
              >
                <Calendar size={12} style={{ marginRight: 5 }} /> Tanggal
                Pelunasan
              </label>
              <input
                type="date"
                style={styles.input}
                value={editingItem.tanggal_bayar || ""}
                onChange={(e) =>
                  setEditingItem({
                    ...editingItem,
                    tanggal_bayar: e.target.value,
                  })
                }
              />
              <p
                style={{
                  fontSize: 11,
                  color: "#64748B",
                  marginTop: -10,
                  marginBottom: 15,
                }}
              >
                * Kosongkan jika belum lunas
              </p>

              <button
                type="submit"
                style={{
                  width: "100%",
                  padding: 10,
                  background: "#2563EB",
                  color: "white",
                  border: "none",
                  borderRadius: 8,
                  fontWeight: "bold",
                  cursor: "pointer",
                }}
              >
                Simpan Perubahan
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
